import {
  ReactNode,
  SyntheticEvent,
  Key,
  useState,
  FC,
  useEffect,
  ComponentType,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { VerticalTabsProvider } from '../VerticalTabsContext';
import Box from '@mui/material/Box';
import MuiTabs from '@mui/material/Tabs';
import MuiTab, { TabProps } from '@mui/material/Tab';
import { styled, Theme } from '@mui/material/styles';
import { Row } from '../Layout';
import { Divider } from '@mui/material';

type StyledDividerBoxProps = {
  spacingY?: keyof Theme['spacingSizes'];
};

const StyledDividerBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'spacingY',
})<StyledDividerBoxProps>(({ theme, spacingY = 'sm' }) => ({
  padding: theme.spacing(theme.spacingSizes[spacingY], 0),
  display: 'flex',
  alignItems: 'stretch',
}));

const Tabs = styled(MuiTabs, {
  shouldForwardProp: (prop) => prop !== 'spacing',
})<{ spacing: keyof Theme['spacingSizes'] | 'none' }>(({ theme, spacing }) => ({
  minHeight: 'unset',
  '& .MuiTabs-list': {
    gap:
      spacing !== 'none' ? theme.spacing(theme.spacingSizes[spacing]) : 'unset',
  },
}));

const Dot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'color',
})<{ color: string }>(({ color }) => ({
  width: 6,
  height: 6,
  borderRadius: '50%',
  backgroundColor: color,
}));

const TabPanel = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'hidden',
})<{ hidden?: boolean }>(({ hidden }) => ({
  display: hidden ? 'none' : 'block',
  width: '100%',
}));

const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(theme.spacingSizes.lg),
}));

const KeepAlivePanel: FC<{ active: boolean; children: ReactNode }> = ({
  active,
  children,
}) => {
  const [mounted, setMounted] = useState(active);

  useEffect(() => {
    if (active) {
      setMounted(true);
    }
  }, [active]);

  if (!mounted) {
    return null;
  }

  return <TabPanel hidden={!active}>{children}</TabPanel>;
};

type Props<T extends Key> = {
  tabs: Array<{
    label: string | ReactNode;
    key: T;
    content: ReactNode;
    urlParam?: string;
    dotColor?: string;
  }>;
  withDivider?: boolean;
  useTabsUrls?: boolean;
  keepAlive?: boolean;
  spacing?: keyof Theme['spacingSizes'] | 'none';
  Tab?: ComponentType<TabProps>;
  defaultTab?: T;
  onTabChange?: (tabKey: T) => void;
};

const TabsPanel = <T extends Key>({
  tabs,
  useTabsUrls,
  keepAlive,
  spacing = 'lg',
  Tab = MuiTab,
  defaultTab,
  withDivider,
  onTabChange,
}: Props<T>): JSX.Element => {
  const { hash } = useLocation();
  const history = useHistory();

  const [currentTab, setCurrentTab] = useState<T | null>(() => {
    if (defaultTab) {
      return defaultTab;
    }

    const tab =
      useTabsUrls && hash
        ? tabs.find((tab) => tab.urlParam === hash.replace('#', ''))
        : null;

    if (tab) {
      return tab.key;
    }

    return tabs[0]?.key ?? null;
  });

  type VerticalTabState = Partial<Record<T, string | null>>;
  const [verticalTabs, setVerticalTabs] = useState<VerticalTabState>({});

  const handleChange = (_: SyntheticEvent, newTabKey: T) => {
    const newTab = tabs.find((tab) => tab.key === newTabKey);

    if (newTab?.urlParam && useTabsUrls) {
      history.push(`#${newTab.urlParam}`);
    } else {
      setCurrentTab(newTabKey);
    }

    onTabChange?.(newTabKey);
  };

  const currentTabContent = tabs.find((tab) => tab.key === currentTab)?.content;

  return (
    <Container>
      <Tabs
        spacing={spacing}
        value={currentTab}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons={false}
      >
        {tabs.flatMap((tab, index) => {
          const isLast = index === tabs.length - 1;

          return [
            <Tab
              key={tab.key}
              label={
                tab.dotColor ? (
                  <Row centered spacing="xs">
                    {tab.label}
                    <Dot color={tab.dotColor} />
                  </Row>
                ) : (
                  tab.label
                )
              }
              value={tab.key}
            />,
            !isLast && withDivider ? (
              <StyledDividerBox key={`${tab.key}-divider`}>
                <Divider key={`${tab.key}-divider`} orientation="vertical" />
              </StyledDividerBox>
            ) : null,
          ];
        })}
      </Tabs>
      <VerticalTabsProvider
        verticalTab={verticalTabs?.[currentTab] ?? null}
        setVerticalTab={(verticalTab) => {
          if (currentTab) {
            setVerticalTabs({ ...verticalTabs, [currentTab]: verticalTab });
          }
        }}
      >
        {keepAlive ? (
          tabs.map((tab) => (
            <KeepAlivePanel key={tab.key} active={currentTab === tab.key}>
              {tab.content}
            </KeepAlivePanel>
          ))
        ) : (
          <TabPanel>{currentTabContent}</TabPanel>
        )}
      </VerticalTabsProvider>
    </Container>
  );
};

export default TabsPanel;
